import { Input, NO_INPUT } from '../sim/car';

/**
 * Rollback netcode (design.md B8.2), GGPO-style, transport-agnostic.
 *
 * Every device runs the same deterministic sim. Your own inputs are applied
 * `inputDelay` ticks late (hiding a little latency for free); other
 * players' inputs are predicted (they keep doing what they last did). When
 * their real inputs arrive and differ, we rewind to that tick and
 * re-simulate. We never run more than `maxPredict` ticks past the last
 * fully-confirmed tick: beyond that we wait. A player who leaves is
 * handed to the AI from an agreed tick (their slot drops out of the
 * input map, and the sim drives that car itself).
 *
 * No DOM, no clocks: the caller says which tick it should be on.
 */

export interface NetSim<S> {
  step(inputs: ReadonlyMap<number, Input>): void;
  save(): S;
  load(s: S): void;
  hash(): number;
}

export interface RollbackOptions {
  /** Racer indices driven by people (local and remote). */
  slots: number[];
  /** Your racer index, or -1 to spectate. */
  local: number;
  inputDelay: number;
  maxPredict: number;
  /** Report a state hash every this many ticks (0 = never). */
  checkEvery?: number;
}

const same = (a: Input, b: Input) => a.steer === b.steer && a.throttle === b.throttle && a.brake === b.brake;

export class RollbackSession<S> {
  /** Next tick to simulate: the sim's state is "after frame - 1". */
  frame = 0;
  /** Re-simulated ticks so far (stats). */
  resimulated = 0;
  private readonly sim: NetSim<S>;
  private readonly opts: Required<RollbackOptions>;
  /** Confirmed inputs per slot, by tick. */
  private readonly inputs = new Map<number, Input[]>();
  /** Inputs actually used per slot, by tick (null = the AI drove it). */
  private readonly used = new Map<number, (Input | null)[]>();
  /** A slot that left: from this tick on its input is "nothing". */
  private readonly gone = new Map<number, number>();
  private readonly snaps = new Map<number, S>();
  private rollbackFrom = Infinity;
  private checked = 0;

  /** Called for every simulated tick (resim = true while replaying after a rollback). */
  onStep: (frame: number, resim: boolean) => void = () => undefined;
  /** Hash of the confirmed state at a checkpoint tick (state before stepping `frame`). */
  onHash: (frame: number, hash: number) => void = () => undefined;

  constructor(sim: NetSim<S>, opts: RollbackOptions) {
    this.sim = sim;
    this.opts = { checkEvery: 0, ...opts };
    for (const s of opts.slots) {
      this.inputs.set(s, []);
      this.used.set(s, []);
    }
    // Everyone's first `inputDelay` ticks are "nothing" (by agreement, never sent).
    for (const s of opts.slots) for (let f = 0; f < opts.inputDelay; f++) this.inputs.get(s)![f] = NO_INPUT;
  }

  /** How many ticks of input we hold for a slot (contiguous from 0). */
  known(slot: number): number {
    if (this.gone.has(slot)) return Infinity;
    return this.inputs.get(slot)!.length;
  }

  /** Every tick below this has every player's real input. */
  confirmedFrame(): number {
    let m = Infinity;
    for (const s of this.opts.slots) m = Math.min(m, this.known(s));
    return m;
  }

  /** Inputs from another player, for ticks from..from+list.length-1 (in order, no gaps). */
  addRemote(slot: number, from: number, list: readonly Input[]): void {
    const arr = this.inputs.get(slot);
    if (!arr || this.gone.has(slot) || slot === this.opts.local) return;
    for (let k = 0; k < list.length; k++) {
      const f = from + k;
      if (f < arr.length) continue; // duplicate
      if (f > arr.length) throw new Error(`input gap for slot ${slot}: have ${arr.length}, got ${f}`);
      arr.push(list[k]);
      const u = this.used.get(slot)![f];
      if (f < this.frame && u !== undefined && (u === null || !same(u, list[k]))) this.rollbackFrom = Math.min(this.rollbackFrom, f);
    }
  }

  /** A player left: the AI drives their car from `from` on. */
  markGone(slot: number, from: number): void {
    if (this.gone.has(slot) || !this.inputs.has(slot)) return;
    const arr = this.inputs.get(slot)!;
    const at = Math.max(from, arr.length);
    arr.length = Math.min(arr.length, at);
    this.gone.set(slot, at);
    const used = this.used.get(slot)!;
    for (let f = at; f < this.frame; f++) {
      if (used[f] !== undefined && used[f] !== null) {
        this.rollbackFrom = Math.min(this.rollbackFrom, f);
        break;
      }
    }
  }

  isGone(slot: number): boolean {
    return this.gone.has(slot);
  }

  private inputFor(slot: number, f: number): Input | null {
    const g = this.gone.get(slot);
    if (g !== undefined && f >= g) return null;
    const arr = this.inputs.get(slot)!;
    if (f < arr.length) return arr[f];
    return arr.length ? arr[arr.length - 1] : NO_INPUT; // prediction: same as last time
  }

  /**
   * Catch up to `target` ticks (rewinding first if a guess was wrong).
   * `sampleLocal(f)` is asked for your input for tick f (= the new tick +
   * inputDelay) and `send(f, input)` should ship it. Returns new ticks run.
   */
  advanceTo(target: number, sampleLocal: (f: number) => Input, send: (f: number, input: Input) => void): number {
    if (this.rollbackFrom < this.frame) {
      const snap = this.snaps.get(this.rollbackFrom);
      if (snap === undefined) throw new Error(`no snapshot for tick ${this.rollbackFrom}`);
      this.sim.load(snap);
      for (let f = this.rollbackFrom; f < this.frame; f++) {
        this.stepFrame(f, true);
        this.resimulated++;
      }
    }
    this.rollbackFrom = Infinity;

    let n = 0;
    const local = this.opts.local;
    while (this.frame < target && this.frame < this.confirmedFrame() + this.opts.maxPredict) {
      if (local >= 0) {
        const lf = this.frame + this.opts.inputDelay;
        const arr = this.inputs.get(local)!;
        if (arr.length === lf) {
          const inp = sampleLocal(lf);
          arr.push(inp);
          send(lf, inp);
        }
      }
      this.stepFrame(this.frame, false);
      this.frame++;
      n++;
    }
    this.checkpoints();
    // Nothing before the confirmed tick can be rewound to any more.
    const keep = Math.min(this.confirmedFrame(), this.frame);
    for (const f of this.snaps.keys()) if (f < keep) this.snaps.delete(f);
    return n;
  }

  private stepFrame(f: number, resim: boolean): void {
    this.snaps.set(f, this.sim.save());
    const map = new Map<number, Input>();
    for (const s of this.opts.slots) {
      const inp = this.inputFor(s, f);
      if (inp) map.set(s, inp);
      this.used.get(s)![f] = inp;
    }
    this.sim.step(map);
    this.onStep(f, resim);
  }

  /** Hash confirmed checkpoints (for desync detection between devices). */
  private checkpoints(): void {
    const every = this.opts.checkEvery;
    if (!every) return;
    const conf = Math.min(this.confirmedFrame(), this.frame - 1);
    while ((this.checked + 1) * every <= conf) {
      const f = (this.checked + 1) * every;
      const snap = this.snaps.get(f);
      if (snap !== undefined) {
        const cur = this.sim.save();
        this.sim.load(snap);
        this.onHash(f, this.sim.hash());
        this.sim.load(cur);
      }
      this.checked++;
    }
  }
}

/**
 * Server clock estimate from ping/pong (NTP-style): keep the samples with
 * the shortest round trip, which carry the least queueing error.
 */
export class ClockSync {
  private samples: { rtt: number; offset: number }[] = [];

  add(sentLocal: number, serverTime: number, recvLocal: number): void {
    const rtt = recvLocal - sentLocal;
    this.samples.push({ rtt, offset: serverTime - (sentLocal + rtt / 2) });
    if (this.samples.length > 12) this.samples.shift();
  }

  get ready(): boolean {
    return this.samples.length > 0;
  }

  get rtt(): number {
    return this.samples.length ? Math.min(...this.samples.map((s) => s.rtt)) : 0;
  }

  /** server time ≈ local time + offset */
  get offset(): number {
    if (!this.samples.length) return 0;
    const best = [...this.samples].sort((a, b) => a.rtt - b.rtt).slice(0, 3);
    return best.reduce((a, s) => a + s.offset, 0) / best.length;
  }
}
